import html2pdf from 'html2pdf.js';

/**
 * html2pdf cuts the canvas at `floor(canvasWidth * pageHeight / pageWidth)`.
 * Page breaks are placed with a different height: the CSS page height times
 * the html2canvas scale. On some displays those differ by about one pixel.
 * The gap stacks on every page, so later pages (around 19 and on) crop the
 * last line of text and paint the rest at the top of the next page.
 *
 * Point the cutter at the same height the page breaks used.
 */
type PdfWorkerContext = {
  prop: {
    canvas: HTMLCanvasElement;
    pageSize: {
      inner: {
        px: { height: number };
        ratio: number;
      };
    };
  };
};

// html2pdf is untyped (declare module). The worker is a thenable that shares
// `prop` across the chain.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PdfWorker = any;

const ratioForSlice = (canvasWidth: number, slicePx: number) => {
  // Stay just above the integer so floor(width * ratio) cannot land on
  // slicePx - 1 from floating-point error, and cannot reach slicePx + 1.
  let ratio = (slicePx + 0.5) / canvasWidth;
  let guard = 0;
  while (Math.floor(canvasWidth * ratio) !== slicePx && guard < 8) {
    const actual = Math.floor(canvasWidth * ratio);
    ratio += (actual < slicePx ? 1 : -1) * (guard + 1) * 1e-6;
    guard += 1;
  }
  return Math.floor(canvasWidth * ratio) === slicePx ? ratio : null;
};

export const renderElementToPdf = async (el: HTMLElement, opt: PdfWorker) => {
  let pageCssHeight = 0;

  const pdfOptions = {
    ...opt,
    html2canvas: {
      ...(opt.html2canvas ?? {}),
      // Captured from a fixed overlay. A scrolled window would shift the
      // canvas relative to the page-break spacers.
      scrollX: 0,
      scrollY: 0,
    },
  };
  const canvasScale = Number(pdfOptions.html2canvas.scale) || 1;

  const pdf = await html2pdf()
    .set(pdfOptions)
    .from(el)
    .toContainer()
    .then(function (this: PdfWorkerContext) {
      pageCssHeight = this.prop.pageSize.inner.px.height;
    })
    .toCanvas()
    .then(function (this: PdfWorkerContext) {
      const canvas = this.prop.canvas;
      const domPagePx = Math.round(pageCssHeight * canvasScale);
      if (canvas.width > 0 && domPagePx > 0) {
        const ratio = ratioForSlice(canvas.width, domPagePx);
        if (ratio != null) {
          this.prop.pageSize.inner.ratio = ratio;
        }
      }
    })
    .toPdf()
    .get('pdf');

  return pdf;
};
